/**
 * The report form's words and rules (build decisions §4): what each kind of
 * report asks, and what goes to Nexsoss.
 */
import type { CreateSupportTicketRequest } from '../../api/v1';
import type { ReportCategory } from '../../state/report-problem';

export const REPORT_CATEGORIES: ReadonlyArray<{
  readonly id: ReportCategory;
  readonly label: string;
  readonly hint: string;
}> = [
  { id: 'robot', label: 'A robot', hint: 'It stopped, went the wrong way, or missed a clean.' },
  { id: 'lift_door', label: 'A lift or door', hint: 'It won’t open, won’t come, or a robot is stuck at it.' },
  { id: 'app', label: 'This app', hint: 'A screen looks wrong or won’t do what you need.' },
  { id: 'other', label: 'Something else', hint: 'Anything else you want Nexsoss to know.' },
];

export const SOMETHING_ELSE = 'Something else';

export const QUICK_PICKS: Readonly<Record<ReportCategory, readonly string[]>> = {
  robot: [
    'Stopped and won’t move',
    'Went the wrong way',
    'Missed or stopped its clean',
    'Stuck at a lift or door',
    SOMETHING_ELSE,
  ],
  lift_door: [
    'A door won’t open',
    'A door won’t close',
    'A lift isn’t coming',
    'A robot is stuck at it',
    SOMETHING_ELSE,
  ],
  app: ['A screen won’t load', 'Something looks wrong', 'I can’t do what I need', SOMETHING_ELSE],
  other: [],
};

export const NOT_SURE = 'Not sure';

/** What the form must have before it can send: words of some kind. A quick
 *  pick says what happened, unless it is "Something else". */
export function canSend(text: string, quickPick: string | null): boolean {
  if (text.trim()) return true;
  return quickPick !== null && quickPick !== SOMETHING_ELSE;
}

const TITLE_MAX = 200;
const TITLE_FROM_TEXT = 80;

export interface ReportAnswers {
  readonly category: ReportCategory;
  readonly subject: string | null;
  readonly quickPick: string | null;
  readonly text: string;
  readonly issueId?: string;
  readonly alertId?: string;
}

/**
 * The ticket. Title: the pick and what it is about ("Stopped and won’t move:
 * NomNom"), or the start of what they wrote. Body: what they wrote, or the pick
 * when that says it all. "Not sure" is an answer, not a subject.
 */
export function ticketFrom(answers: ReportAnswers): CreateSupportTicketRequest {
  const text = answers.text.trim();
  const pick = answers.quickPick && answers.quickPick !== SOMETHING_ELSE ? answers.quickPick : null;
  const subject = answers.subject && answers.subject !== NOT_SURE ? answers.subject : null;
  const head =
    pick ??
    (text.length > TITLE_FROM_TEXT ? `${text.slice(0, TITLE_FROM_TEXT).trimEnd()}…` : text) ??
    '';
  const title = subject && head ? `${head}: ${subject}` : head || subject || 'Report';
  return {
    title: title.slice(0, TITLE_MAX),
    body: text || pick || title,
    category: answers.category,
    ...(subject ? { subject } : {}),
    ...(answers.quickPick ? { quick_pick: answers.quickPick } : {}),
    ...(answers.issueId ? { issue_id: answers.issueId } : {}),
    ...(answers.alertId ? { alert_id: answers.alertId } : {}),
  };
}

/**
 * What goes with a report, and nothing it does not: the list the form shows is
 * what the server's snapshot takes (`api/tickets.py::_capture_snapshot`). The
 * robot's last half hour is in the logs, not on the ticket, so that line says
 * Nexsoss reads it.
 */
export function attachmentLines(subjectRobot: string | null): string[] {
  return [
    'What the robots, lifts and doors are doing as you press Send',
    'Any alerts open right now',
    'Your name, this site and the time',
    ...(subjectRobot ? [`Nexsoss can read ${subjectRobot}’s last 30 minutes from its logs`] : []),
  ];
}
